/**
 * Unfinished forms kept in this browser, so a refresh or a lost connection does not
 * lose what was typed. Patient data on a possibly shared computer: a draft belongs to
 * one user, is removed when the form is submitted or the user signs out, and expires.
 */
import moment from "moment";

const PREFIX = "enlis:draft:";
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const MOMENT_TAG = "__draftMoment";

const keyOf = (userId, formId) => `${PREFIX}${userId}:${formId}`;

// antd date pickers hold moment objects, which JSON would turn into plain strings.
const pack = (value) => {
  if (moment.isMoment(value)) return { [MOMENT_TAG]: value.toISOString() };
  if (Array.isArray(value)) return value.map(pack);
  if (value && typeof value === "object") {
    if (typeof File !== "undefined" && value instanceof File) return undefined;
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, pack(item)]),
    );
  }
  return value;
};

const unpack = (value) => {
  if (Array.isArray(value)) return value.map(unpack);
  if (value && typeof value === "object") {
    if (value[MOMENT_TAG]) return moment(value[MOMENT_TAG]);
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, unpack(item)]),
    );
  }
  return value;
};

/** The saved text, also used to tell whether anything changed since the last save. */
export const packDraft = (data) => JSON.stringify(pack(data));

/** False when the browser refuses (storage full or blocked); the form still works. */
export const saveDraft = (userId, formId, packed, now = Date.now()) => {
  try {
    localStorage.setItem(keyOf(userId, formId), JSON.stringify({ savedAt: now, packed }));
    return true;
  } catch (error) {
    return false;
  }
};

/** { savedAt, data }, or null when there is none or it has expired. */
export const loadDraft = (userId, formId, now = Date.now()) => {
  try {
    const raw = localStorage.getItem(keyOf(userId, formId));
    if (!raw) return null;
    const { savedAt, packed } = JSON.parse(raw);
    if (now - savedAt > MAX_AGE_MS) {
      localStorage.removeItem(keyOf(userId, formId));
      return null;
    }
    return { savedAt, data: unpack(JSON.parse(packed)) };
  } catch (error) {
    return null;
  }
};

export const removeDraft = (userId, formId) => {
  try {
    localStorage.removeItem(keyOf(userId, formId));
  } catch (error) {
    // nothing stored, or storage is blocked
  }
};

/** Every user's drafts on this browser: signing out ends access to them. */
export const clearAllDrafts = () => {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(PREFIX))
      .forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    // storage is blocked
  }
};
